import { type ReactNode } from 'react'

interface EmptyStateProps {
  /** Tiny uppercase mono label above the headline. */
  eyebrow: string
  /** Headline rendered in the display font. */
  title: string
  /** One-paragraph supporting copy. */
  description: string
  /** Optional CTA (button, link). Rendered below the description. */
  action?: ReactNode
}

/**
 * Shared empty-state panel used across admin pages: dashed bordered card,
 * faint hawk-grid backdrop, eyebrow + headline + body + optional action.
 * Reach for this whenever a list, table, or search result has nothing to show.
 */
export function EmptyState({ eyebrow, title, description, action }: EmptyStateProps) {
  return (
    <div className="relative overflow-hidden border border-border border-dashed rounded-md py-16 px-8 text-center bg-card/30">
      <div
        className="absolute inset-0 hawk-grid hawk-fade-radial pointer-events-none"
        aria-hidden
      />
      <div className="relative">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
          {eyebrow}
        </div>
        <p className="font-display text-2xl tracking-tight mb-2 text-foreground/80">
          {title}
        </p>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          {description}
        </p>
        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  )
}
